/**
 * Pricing Grid Component
 * Billing toggle and grid of pricing cards
 */

import { useState } from 'react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { PRICING_TIERS, YEARLY_DISCOUNT_PERCENT } from '../utils/constants';
import { PricingCard } from './PricingCard';
import type { BillingPeriod, SubscriptionTier, SubscriptionSource } from '../types';

interface PricingGridProps {
  currentTier: SubscriptionTier;
  subscriptionSource: SubscriptionSource;
  isLoading: boolean;
  onUpgrade: (tier: 'pro' | 'pro_plus') => void;
}

export function PricingGrid({
  currentTier,
  subscriptionSource,
  isLoading,
  onUpgrade,
}: PricingGridProps) {
  const { t } = useLanguage();
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');

  return (
    <div className="animate-fade-in animation-delay-200">
      {/* Billing Toggle */}
      <div className="flex justify-center mb-8">
        <div className="inline-flex items-center gap-3 p-1.5 bg-gray-100 rounded-xl">
          <button
            onClick={() => setBillingPeriod('monthly')}
            className={`
              px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200
              ${billingPeriod === 'monthly'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
              }
            `}
          >
            {t('upgrade.billing.monthly', 'Monthly')}
          </button>
          <button
            onClick={() => setBillingPeriod('yearly')}
            className={`
              px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2
              ${billingPeriod === 'yearly'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
              }
            `}
          >
            {t('upgrade.billing.yearly', 'Yearly')}
            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
              -{YEARLY_DISCOUNT_PERCENT}%
            </span>
          </button>
        </div>
      </div>

      {/* Pricing Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-4 lg:gap-6 items-start">
        {PRICING_TIERS.map((tier, index) => (
          <PricingCard
            key={tier.id}
            tier={tier}
            billingPeriod={billingPeriod}
            currentTier={currentTier}
            subscriptionSource={subscriptionSource}
            isLoading={isLoading}
            onUpgrade={onUpgrade}
            animationDelay={index * 100}
          />
        ))}
      </div>

      {/* Trial note */}
      <p className="text-center text-sm text-gray-500 mt-8">
        {t('upgrade.trialNote', 'All paid plans include a 7-day free trial. No credit card required to start.')}
      </p>
    </div>
  );
}
